import { getCurrentInstance, ref, computed, Ref, nextTick } from '@vue/composition-api';
import { TranslateResult } from 'vue-i18n';
import { useBehaviorData } from './../../../../hooks/creation/adgroup/useBehavior/index';
import { i18n } from './../../../../i18n';
import {
  InterestBehaviorScene,
  InterestBehaviorData,
  AdgroupFormData,
  TargetingKeywordsSearchStatus,
} from '@tt4b/ttam-enums';
import { useAdgroupAlertInfo } from '@tt4b/ttam-hooks';
// 下线的提示配置
const ConfigGeneratorMap = {
  [InterestBehaviorScene.HASHTAG]: (count: number, offlineData: InterestBehaviorData[]) => ({
    key: 'behavior-hashtag',
    label: 'module_adgroup_behavior_behavior_label',
    message: 'adgroup_target_hashtag_auto_remove_hint',
    button: 'adgroup_target_hashtag_auto_remove_hint_button',
    link: '',
    count,
    dialogTitle: 'adgroup_target_hashtag_removed_diag_title',
    dialogDesc: i18n.t('adgroup_target_hashtag_removed_diag_desc', {
      num: count,
    }),
    data: offlineData,
  }),
  [InterestBehaviorScene.KEYWORD]: (count: number, offlineData: InterestBehaviorData[], reason: string) => ({
    key: 'behavior-hashtag-1',
    label: 'module_adgroup_behavior_interest_label',
    message: 'adgroup_target_interest_auto_remove_hint',
    reason,
    button: '',
    link: 'adgroup_target_interest_auto_remove_hint_link_plural',
    count,
    dialogTitle: 'adgroup_target_hashtag_removed_diag_title',
    dialogDesc: i18n.t('adgroup_target_interest_auto_remove_hint', {
      link: i18n.t('adgroup_target_interest_auto_remove_hint_link_plural', { num: count }),
      reason: i18n.t(reason),
    }),
    data: offlineData,
  }),
};
const dialogConfig = ref<{
  title: TranslateResult;
  data: InterestBehaviorData[];
  description: TranslateResult;
  isShow;
}>({ title: '', data: [], description: '', isShow: ref(false) });

export function useOfflineTags(props: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = props;
  const {
    hashtagCategories,
    getClearDataByType: clearDataByType,
    changeHashtagCategories,
  } = useBehaviorData({
    adgroupData,
  });
  const showRemovedOfflineTagsDialog = ref(false);
  const { addAlertInfo } = useAdgroupAlertInfo();
  const createElement = getCurrentInstance()?.proxy.$createElement;
  // 设置下线的alert
  function setOfflineAlert(alertInfoMessage) {
    if (createElement) {
      addAlertInfo({
        key: alertInfoMessage.key,
        message: {
          label: i18n.t(alertInfoMessage.label),
          content: createElement('i18n', { attrs: { path: alertInfoMessage.message } }, [
            createElement('span', {
              attrs: { place: 'reason' },
              domProps: { innerText: alertInfoMessage.reason ? i18n.t(alertInfoMessage.reason) : '' },
            }),
            createElement('a', {
              on: {
                click: () => {
                  dialogConfig.value = {
                    title: i18n.t(alertInfoMessage.dialogTitle),
                    data: alertInfoMessage.data,
                    description: alertInfoMessage.dialogDesc,
                    isShow: ref(true),
                  };
                },
              },
              attrs: { place: alertInfoMessage.button ? 'button' : 'link' },
              domProps: {
                innerHTML: i18n.t(alertInfoMessage.button || alertInfoMessage.link, {
                  num: alertInfoMessage.count,
                  count: alertInfoMessage.count,
                }),
              },
            }),
          ]),
        },
      });
    }
  }
  // 移除offline hashtag
  function removeHashtagOfflineTags(list, setFormDataFun?: (data: any) => void) {
    if (!list?.length) {
      return;
    }

    const offlineTagsSet = new Set(list.map(tag => tag?.tag_id));
    const newHashtags = hashtagCategories.value.filter(id => !offlineTagsSet.has(id));
    const data = changeHashtagCategories(newHashtags);
    setFormDataFun?.(data);
    if (!newHashtags.length) {
      clearDataByType('hashtag');
    }
    const alertInfoMessage = ConfigGeneratorMap[InterestBehaviorScene.HASHTAG](list.length, list);

    setOfflineAlert(alertInfoMessage);
  }

  function handleHashtagOffline(list, setFormDataFun?: (data: any) => void) {
    const offlineHashtags = list.filter(item =>
      [TargetingKeywordsSearchStatus.OFFLINE, TargetingKeywordsSearchStatus.LEGAL_OFFLINE].includes(
        item.status || TargetingKeywordsSearchStatus.ONLINE,
      ),
    );
    removeHashtagOfflineTags(offlineHashtags, setFormDataFun);
  }
  // 移除 下线的 Interest
  function removeInterestKeywordsOfflineTags(
    list,
    status: TargetingKeywordsSearchStatus,
    setFormDataFun?: (data: any) => void,
  ) {
    if (!list?.length) {
      return;
    }
    let alertInfoMessage = {};
    const offlineTagsSet = new Set(list.map(tag => tag.tag_id));
    const _interestKeywordsI18n: number[] = [];
    const _interestKeywordsLangI18n: string[] = [];
    adgroupData.value.interestKeywordsI18n.forEach((id, index) => {
      if (!offlineTagsSet.has(id)) {
        _interestKeywordsI18n.push(id);
        _interestKeywordsLangI18n.push(adgroupData.value.interestKeywordsLangI18n[index]);
      }
    });
    setFormDataFun?.({
      interestKeywordsI18n: _interestKeywordsI18n,
      interestKeywordsLangI18n: _interestKeywordsLangI18n,
    });

    if (status === TargetingKeywordsSearchStatus.OFFLINE) {
      alertInfoMessage = ConfigGeneratorMap[InterestBehaviorScene.KEYWORD](
        list.length,
        list,
        'adgroup_target_interest_auto_remove_hint_reason_drop',
      );
    } else if (status === TargetingKeywordsSearchStatus.LEGAL_OFFLINE) {
      alertInfoMessage = ConfigGeneratorMap[InterestBehaviorScene.KEYWORD](
        list.length,
        list,
        'adgroup_target_interest_auto_remove_hint_reason_legal',
      );
    }
    setOfflineAlert(alertInfoMessage);
  }
  // 移除下线的InterestKeywords
  function handleInterestKeywordsOffline(list, setFormDataFun?: (data: any) => void) {
    const offlineInterestKeywords = list.filter(item => item.status === TargetingKeywordsSearchStatus.OFFLINE);
    removeInterestKeywordsOfflineTags(offlineInterestKeywords, TargetingKeywordsSearchStatus.OFFLINE, setFormDataFun);

    const legalOfflineInterestKeywords = list.filter(
      item => item.status === TargetingKeywordsSearchStatus.LEGAL_OFFLINE,
    );
    nextTick(() => {
      removeInterestKeywordsOfflineTags(
        legalOfflineInterestKeywords,
        TargetingKeywordsSearchStatus.LEGAL_OFFLINE,
        setFormDataFun,
      );
    });
  }

  return {
    dialogConfig,
    showRemovedOfflineTagsDialog,
    handleInterestKeywordsOffline,
    handleHashtagOffline,
  };
}
